import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { toggleFavorite } from '../../store/slices/favoritesSlice';
import { scoresAPI } from '../../api/itemEngagementAPI';
import { trackItemFavorited } from '../../utils/analytics';
import {
  buildFavoriteKey,
  favoriteKindToAnalyticsType,
  favoriteKindToScoresType,
  isFavoriteKey,
} from '../../utils/favoriteKeys';
import { navigateRequireAuth } from '../../utils/authRedirect';

const Button = styled.button`
  position: ${(p) => (p.$overlay ? 'absolute' : 'relative')};
  top: ${(p) => (p.$overlay ? '0.5rem' : 'auto')};
  right: ${(p) => (p.$overlay ? '0.5rem' : 'auto')};
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  cursor: pointer;
  color: ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--text-secondary)')};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 5;

  &:hover {
    transform: scale(1.08);
  }
`;

const ItemFavoriteButton = ({
  itemId,
  favoriteKind = 'item',
  category,
  overlay = true,
  className,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const favoriteItems = useSelector((state) => state.favorites.favoriteItems);
  const isFavorited = isFavoriteKey(favoriteItems, favoriteKind, itemId);

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isAuthenticated) {
      navigateRequireAuth(navigate, location);
      return;
    }
    const key = buildFavoriteKey(favoriteKind, itemId);
    dispatch(toggleFavorite(key));
    toast[isFavorited ? 'info' : 'success'](
      isFavorited ? 'Removed from favorites' : 'Added to favorites',
    );
    if (!isFavorited) {
      const analyticsType = favoriteKindToAnalyticsType(favoriteKind);
      trackItemFavorited(itemId, { item_type: analyticsType, category });
      scoresAPI
        .recordFavorite(itemId, favoriteKindToScoresType(favoriteKind))
        .catch(() => {});
    }
  };

  return (
    <Button
      type="button"
      className={className}
      $overlay={overlay}
      $active={isFavorited}
      aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      onClick={handleClick}
    >
      {isFavorited ? '❤️' : '🤍'}
    </Button>
  );
};

export default ItemFavoriteButton;
